// Single source of truth for mapping a customer's plan onto a landing route and a
// menu role. Login, auth-check, pricing, checkout success, email verification and
// the route guards all resolve through here so they cannot drift apart.

export type PlanProduct = 'hybrid' | 'agents' | 'api' | 'recruiting' | 'enterprise' | 'unknown';

export interface PlanContext {
  kundennummer?: number | string | null;
  canAccessHybrid?: boolean;
}

export const POOL_AGENTS_CUSTOMER_NUMBER = 100001;
export const POOL_RECRUITING_CUSTOMER_NUMBER = 100002;

export const PRICING_ROUTE = '/pricing';
export const HYBRID_DASHBOARD_ROUTE = '/hybrid-dashboard';
export const AGENT_DASHBOARD_ROUTE = '/agent-dashboard';
export const DASHBOARD_ROUTE = '/dashboard';

export const FREE_AGENTS_PLANS = ['agents-free', 'free'];
export const PAID_AGENTS_PLANS = ['agents-pro', 'agents-ultra', 'agents-enterprise'];

export const FREE_HYBRID_PLANS = ['hybrid-free'];
export const PAID_HYBRID_PLANS = ['hybrid-starter', 'hybrid-discovery', 'hybrid-pro', 'hybrid-scale'];

export const PAID_API_PLANS = ['api-starter', 'api-pro', 'api-scale', 'api-enterprise'];
export const PAYG_API_PLANS = ['api-payg'];

export const PAID_RECRUITING_PLANS = [
  'recruiting-25-self-service', 'recruiting-50-self-service', 'recruiting-100-self-service',
  'recruiting-and-sales-25-self-service', 'recruiting-and-sales-50-self-service', 'recruiting-and-sales-100-self-service',
  'recruiting-sales-25-self-service-interview', 'recruiting-sales-25-self-service-1call',
  'recruiting-sales-25-self-service-2calls', 'recruiting-sales-25-self-service-3calls',
  'recruiting-sales-50-self-service-interview', 'recruiting-sales-50-self-service-1call',
  'recruiting-sales-50-self-service-2calls', 'recruiting-sales-50-self-service-3calls',
  'recruiting-sales-100-self-service-interview', 'recruiting-sales-100-self-service-1call',
  'recruiting-sales-100-self-service-2calls', 'recruiting-sales-100-self-service-3calls'
];

export function normalizePlan(plan: any): string {
  return typeof plan === 'string' ? plan.trim().toLowerCase() : '';
}

export function toCustomerNumber(kundennummer: any): number {
  if (typeof kundennummer === 'number') {
    return isNaN(kundennummer) ? 0 : kundennummer;
  }
  const parsed = parseInt(String(kundennummer === null || kundennummer === undefined ? '' : kundennummer), 10);
  return isNaN(parsed) ? 0 : parsed;
}

export function isPoolCustomer(kundennummer: any): boolean {
  const kn = toCustomerNumber(kundennummer);
  return kn === POOL_AGENTS_CUSTOMER_NUMBER || kn === POOL_RECRUITING_CUSTOMER_NUMBER;
}

// Enterprise customers have their own dedicated kundennummer. 0 means "not yet
// provisioned" and must never be treated as enterprise.
export function isEnterpriseCustomerNumber(kundennummer: any): boolean {
  const kn = toCustomerNumber(kundennummer);
  return kn !== 0 && !isPoolCustomer(kn);
}

// ARS is the product name for the hybrid plans, so both spellings count.
export function isHybridPlan(plan: any): boolean {
  const normalized = normalizePlan(plan);
  return normalized.includes('hybrid') || normalized.includes('ars');
}

export function resolvePlanProduct(plan: any): PlanProduct {
  const normalized = normalizePlan(plan);
  if (!normalized) {
    return 'unknown';
  }
  if (isHybridPlan(normalized)) {
    return 'hybrid';
  }
  if (normalized.includes('agent') || FREE_AGENTS_PLANS.includes(normalized)) {
    return 'agents';
  }
  if (normalized.includes('api')) {
    return 'api';
  }
  if (normalized.includes('recruiting') || normalized.includes('self-service')) {
    return 'recruiting';
  }
  if (normalized.includes('enterprise')) {
    return 'enterprise';
  }
  return 'unknown';
}

export function isFreePlan(plan: any): boolean {
  const normalized = normalizePlan(plan);
  if (!normalized) {
    return false;
  }
  return normalized.includes('free') || PAYG_API_PLANS.includes(normalized);
}

export function isPaidPlan(plan: any): boolean {
  const normalized = normalizePlan(plan);
  return PAID_AGENTS_PLANS.includes(normalized) ||
    PAID_HYBRID_PLANS.includes(normalized) ||
    PAID_API_PLANS.includes(normalized) ||
    PAID_RECRUITING_PLANS.includes(normalized);
}

// An empty plan is not an error state, it means the customer never completed
// plan selection and has to be sent to pricing rather than into the product.
export function resolveRouteForPlan(plan: any, context: PlanContext = {}): string {
  const normalized = normalizePlan(plan);
  if (!normalized) {
    return PRICING_ROUTE;
  }
  if (isHybridPlan(normalized)) {
    return HYBRID_DASHBOARD_ROUTE;
  }
  // Enterprise customers keep /dashboard as their landing page. When they also
  // hold the hybrid addon, ARS stays reachable through the enterprise-hybrid menu.
  if (isEnterpriseCustomerNumber(context.kundennummer)) {
    return DASHBOARD_ROUTE;
  }
  if (resolvePlanProduct(normalized) === 'agents') {
    return AGENT_DASHBOARD_ROUTE;
  }
  return DASHBOARD_ROUTE;
}

export function resolveMenuRoleForPlan(plan: any, context: PlanContext = {}): string {
  const normalized = normalizePlan(plan);
  const canAccessHybrid = !!context.canAccessHybrid;

  if (isEnterpriseCustomerNumber(context.kundennummer)) {
    return canAccessHybrid ? 'enterprise-hybrid' : 'enterprise';
  }
  if (isHybridPlan(normalized)) {
    return 'hybrid';
  }

  switch (resolvePlanProduct(normalized)) {
    case 'agents':
      if (FREE_AGENTS_PLANS.includes(normalized)) {
        return 'agents-free';
      }
      if (normalized.includes('enterprise')) {
        return canAccessHybrid ? 'agents-enterprise-hybrid' : 'agents-enterprise';
      }
      if (normalized.includes('ultra')) {
        return 'agents-ultra';
      }
      if (normalized.includes('pro') || normalized.includes('paid') || normalized.includes('starter')) {
        return 'agents-pro';
      }
      return 'agents';
    case 'recruiting':
      return 'self-service-recruiting';
    case 'api':
      return 'pro';
    case 'enterprise':
      return canAccessHybrid ? 'enterprise-hybrid' : 'enterprise';
    default:
      return 'pro';
  }
}

// Only plans without their own in-product onboarding are sent to /user-onboarding.
// Hybrid onboards inside the hybrid dashboard, recruiting inside /dashboard.
export function requiresUserOnboarding(plan: any, kundennummer: any): boolean {
  const normalized = normalizePlan(plan);
  if (isEnterpriseCustomerNumber(kundennummer)) {
    return false;
  }
  if (isHybridPlan(normalized)) {
    return false;
  }
  if (PAID_AGENTS_PLANS.includes(normalized) || PAID_API_PLANS.includes(normalized)) {
    return false;
  }
  if (PAID_RECRUITING_PLANS.includes(normalized) || normalized.includes('self-service')) {
    return false;
  }
  return true;
}
